import { Rating, CircularProgress } from "@mui/material";
import React, { useState, useEffect } from "react";
import "./Feedback.css";

function Feedback() {
    const [rating, setRating] = useState(0);
    const [feedback, setFeedback] = useState("");
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState("");
    const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

    useEffect(() => {
        const checkRecaptcha = () => {
            if (window.grecaptcha && window.grecaptcha.ready) {
                window.grecaptcha.ready(() => {
                    setRecaptchaLoaded(true);
                });
            } else {
                setTimeout(checkRecaptcha, 100);
            }
        };
        checkRecaptcha();
    }, []);

    const getRecaptchaToken = async () => {
        if (!recaptchaLoaded || !window.grecaptcha) {
            throw new Error('reCAPTCHA not loaded');
        }
        
        const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6Le-XGIrAAAAAO0CVRyTzGepFgj_LfB8l67R7kGE';
        if (!siteKey) {
            throw new Error('reCAPTCHA site key not configured');
        }
        
        try {
            const token = await window.grecaptcha.execute(siteKey, {
                action: 'feedback_submit'
            });
            
            if (!token) {
                throw new Error('Failed to generate reCAPTCHA token');
            }
            
            return token;
        } catch (error) {
            console.error('reCAPTCHA execution error:', error);
            throw new Error('reCAPTCHA verification failed');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!rating || !feedback.trim()) {
            setStatus("Please provide both a rating and feedback before submitting.");
            setTimeout(() => setStatus(""), 3000);
            return;
        }

        setLoading(true);
        setStatus("Verifying request...");

        try {
            // Get reCAPTCHA token
            let recaptchaToken;
            try {
                recaptchaToken = await getRecaptchaToken();
                console.log('reCAPTCHA token generated successfully');
            } catch (recaptchaError) {
                console.error('reCAPTCHA error details:', recaptchaError);
                setStatus("❌ Security verification failed. Please refresh the page and try again.");
                setLoading(false);
                return;
            }

            setStatus("Sending your feedback...");

            // Build the payload
            const payload = {
                recaptchaToken,
                rating,
                feedback: feedback.trim(),
                timestamp: new Date().toISOString(),
                userAgent: navigator.userAgent,
                isFeedback: true // Flag to differentiate from design submissions
            };

            // Submit to backend
            const res = await fetch("/submit", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            
            if (res.ok) {
                // Push feedback submission event to Google Tag Manager
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: 'visualiser_feedback_submitted'
                });
                
                setStatus("✅ Thank you for your feedback! We appreciate your input.");
                setRating(0);
                setFeedback("");
                setTimeout(() => {
                    setStatus("");
                }, 5000);
            } else {
                const errorText = await res.text();
                console.error('Server response:', res.status, errorText);
                
                if (res.status === 403) {
                    setStatus("❌ Security verification failed. Please refresh the page and try again.");
                } else {
                    setStatus("❌ Failed to send feedback. Please try again.");
                }
                throw new Error(`Server error: ${res.status} - ${errorText}`);
            }
        } catch (err) {
            console.error("Feedback submission error:", err);
            if (!status.includes("❌")) {
                setStatus("❌ Failed to send feedback. Please try again later.");
            }
        } finally {
            setLoading(false);
        }
    };

  return (
    <>
      <section className="feedback mt-5" style={{paddingBottom: '3rem'}}>
        <div className="container">
          <div className="row">
            <div className="col-12 col-lg-8 m-auto">
              <h2>We Value Your Feedback</h2>
              <p>
                Help us improve! Share your experience with the Gliderol
                Visualiser to make it even better
              </p>
              <form onSubmit={handleSubmit}>
                <div className="rating">
                  <p className="mb-0">Rating *</p>
                  <Rating
                    name="feedback-rating"
                    value={rating}
                    onChange={(_, newValue) => {
                      console.log('Rating changed to:', newValue);
                      setRating(newValue || 0);
                    }}
                    max={5}
                    precision={1}
                    size="large"
                    sx={{
                      fontSize: "2.5rem",
                      '& .MuiRating-iconFilled': {
                        color: '#faaf00',
                      },
                      '& .MuiRating-iconHover': {
                        color: '#faaf00',
                      },
                      '& .MuiRating-iconEmpty': {
                        color: '#e0e0e0',
                      }
                    }}
                    disabled={loading}
                  />
                </div>
                <p className="mt-4">
                Tell us what you think! *
                </p>
                <textarea 
                  rows={4} 
                  className="w-100"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Please share your experience with the visualiser..."
                  disabled={loading}
                  style={{ padding: '10px' }}
                />
                <button 
                  type="submit"
                  className="btn feedbtn mt-3" 
                  disabled={loading || !rating || !feedback.trim()}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={20} style={{ marginRight: '10px', color: 'white' }} />
                      Sending...
                    </>
                  ) : (
                    'Leave Feedback'
                  )}
                </button>
                {status && (
                  <div 
                    className={`mt-3 p-3 text-center rounded ${
                      status.includes('✅') ? 'bg-success text-white' : 
                      status.includes('❌') ? 'bg-danger text-white' : 
                      'bg-warning'
                    }`}
                  >
                    {status}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Feedback;
